/**
 * Simple cache class to improve API performance
 * @param {Object} initialDataSet Data object to use to initialize Cache class
 */
class Cache {
  /**
   * Initializes Cache class with an optional data set
   * @param {Object} initialDataSet Data set to initialize Cache with
   */
  constructor(initialDataSet = {}) {
    this._data = initialDataSet
  }

  /**
   * Retrieves cached data for the passed in cache key
   * @param {String} key The key to use for retrieving cached data
   */
  getCache(key) {
    return this._data[key]
  }

  /**
   * Cache the passed in data with the passed in key
   * @param {String} key The key to use for storing the data for later retrieval
   * @param {Object} data The data to store and associate with the passed in key
   */
  cache(key, data) {
    this._data[key] = data
  }

  /**
   * Clears cache for the passed in key, or the entire cache if no key is passed
   * @param {String} key (Optional) The specific key for which to clear cache data, if passed
   */
  clear(key = null) {
    if (key) this._data[key] = {}
    else this._data = {}
  }

  /**
   * Attempts to retrieve an item from the cache with the given key, or triggers an async callback
   * @param {String} key The cache key
   * @param {Function} callback The callback function to apply once the async function completes
   * @param {Object} scope The scope of the callback function
   * @param {...any} args Any args that you want passed to the callback
   */
  async tryGetAsyncResult(key, callback, scope, ...args) {
    // Check cache first
    let result = this.getCache(key)

    // Cache hit
    if (result) return result

    // Cache result is empty, so let's retrieve and cache it for future use
    try {
      result = await callback.apply(scope, args)
      this.cache(key, result)
    } catch (e) {
      result = e
    }

    return result
  }
}

export default Cache
